import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import type {
  IUkhoNoticeRow,
  IUkhoParkEventRow,
  IVesselNoticeView,
} from "interfaces";
import {
  daysAgo,
  formatDay,
  shortServices,
  UkhoDialogTarget,
} from "../../vessel-transfer/ukho/lib";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Pure helpers of the vessel page's "UKHO transfers" block (design 11.1): the
 * status line, the order of the table rows and the texts of its cells. Days
 * are YYYY-MM-DD text (UKHO's days); `today` comes from the caller.
 */

export { formatDay, shortServices };

export interface UkhoStatus {
  tone: "red" | "green" | "grey" | "none";
  /** The lead sentence without its full stop ("Left per UKHO on 16 Jul 2026"); empty without a notice. */
  title: string;
  text: string;
  /** The button of a red line: "Park with this date" or "Reactivate with this date". */
  action: "park" | "reactivate" | null;
  /** The UKHO day of the latest AVCS-moving notice, YYYY-MM-DD. */
  day: string | null;
  transferId: string | null;
}

/** A row of the table: a UKHO notice, or a park or reactivation done in eMariner. */
export type UkhoTimelineEntry = IUkhoNoticeRow | IUkhoParkEventRow;

export const isUkhoNotice = (
  entry: UkhoTimelineEntry
): entry is IUkhoNoticeRow => entry.kind === "in" || entry.kind === "out";

/**
 * Design section 14's order of the events of one day, earliest first: UKHO
 * transfer-in, UKHO transfer-out, eMariner reactivation, eMariner park.
 */
const SAME_DAY_ORDER: Record<UkhoTimelineEntry["kind"], number> = {
  in: 0,
  out: 1,
  reactivation: 2,
  park: 3,
};

const entryDay = (entry: UkhoTimelineEntry): string =>
  isUkhoNotice(entry) ? entry.noticeDate : entry.day;

const entryId = (entry: UkhoTimelineEntry): number =>
  isUkhoNotice(entry) ? entry.id : entry.vesselTransferId;

/**
 * Newest first. On one day the later event comes first: out after in (a
 * leaving notice is later than an arriving one of the same day), eMariner's
 * reactivation and park after both notices; then the higher id.
 */
export const compareNoticeOrder = (
  a: UkhoTimelineEntry,
  b: UkhoTimelineEntry
): number => {
  const dayA = entryDay(a);
  const dayB = entryDay(b);
  if (dayA !== dayB) {
    return dayA < dayB ? 1 : -1;
  }
  return (
    SAME_DAY_ORDER[b.kind] - SAME_DAY_ORDER[a.kind] || entryId(b) - entryId(a)
  );
};

/** The table's rows: the view's notices (every service) and its park events, newest first. */
export const ukhoTimeline = (view: IVesselNoticeView): UkhoTimelineEntry[] =>
  [...view.notices, ...view.parkEvents].sort(compareNoticeOrder);

/** The notice that drives the status line: the latest one that moves AVCS. */
const latestAvcsNotice = (
  notices: IUkhoNoticeRow[]
): IUkhoNoticeRow | undefined =>
  notices.filter((notice) => notice.movesAvcs).sort(compareNoticeOrder)[0];

/**
 * The day of the latest park event in the view. Null when the view lists no
 * park (the park is older than its range).
 */
const latestParkDay = (parkEvents: IUkhoParkEventRow[]): string | null =>
  parkEvents.filter((event) => event.kind === "park").sort(compareNoticeOrder)[0]
    ?.day ?? null;

const daysAgoText = (day: string, today: Date): string => {
  const days = daysAgo(day, today);
  return days === 1 ? "1 day ago" : `${days} days ago`;
};

/** The status line of the block (design 11.1); only AVCS-moving notices count. */
export const ukhoStatus = (
  view: IVesselNoticeView,
  today: Date
): UkhoStatus => {
  const latest = latestAvcsNotice(view.notices);
  if (!latest) {
    const year = today.getFullYear();
    return {
      tone: "none",
      title: "",
      // A view with notices that move no AVCS (ARCS only, say) is not a view without notices.
      text:
        view.notices.length > 0
          ? `No UKHO notice moving AVCS for this ship in ${year}.`
          : `No UKHO transfer notice for this ship in ${year}.`,
      action: null,
      day: null,
      transferId: null,
    };
  }

  const { parkedNow, onboardedOn } = view.vessel;
  const notice = { day: latest.noticeDate, transferId: latest.transferId };
  const day = formatDay(latest.noticeDate);

  if (latest.kind === "out" && !parkedNow) {
    return {
      ...notice,
      tone: "red",
      title: `Left per UKHO on ${day}`,
      text: `${daysAgoText(latest.noticeDate, today)}, not parked in eMariner. The flat fee report already stops counting this ship on that day.`,
      action: "park",
    };
  }
  if (latest.kind === "out") {
    const parkDay = latestParkDay(view.parkEvents);
    return {
      ...notice,
      tone: "grey",
      title: `Left per UKHO on ${day}`,
      text: parkDay
        ? `Parked in eMariner on ${formatDay(parkDay)}.`
        : "Parked in eMariner.",
      action: null,
    };
  }
  if (parkedNow) {
    return {
      ...notice,
      tone: "red",
      title: `Back per UKHO on ${day}`,
      text: `${daysAgoText(latest.noticeDate, today)}, still under Deactivated Vessels. The report counts it again from that day.`,
      action: "reactivate",
    };
  }
  return {
    ...notice,
    tone: "green",
    title: `With us since ${day}`,
    text: onboardedOn
      ? `Per UKHO notice ${latest.transferId}; onboarded ${formatDay(onboardedOn)}.`
      : `Per UKHO notice ${latest.transferId}.`,
    action: null,
  };
};

/** What the park and reactivate dialogs need: the view's vessel and the status line's notice. */
export const ukhoDialogTarget = (
  view: IVesselNoticeView,
  status: UkhoStatus
): UkhoDialogTarget | null => {
  if (status.day === null || status.transferId === null) {
    return null;
  }
  const { vessel } = view;
  return {
    vesselId: vessel.id,
    vesselName: vessel.name,
    imoNumber: vessel.imoNumber,
    customer: vessel.customer,
    previousCustomer: vessel.previousCustomer,
    previousShipcoId: vessel.previousShipcoId,
    day: status.day,
    transferId: status.transferId,
  };
};

const WHAT_TEXT: Record<UkhoTimelineEntry["kind"], string> = {
  in: "arrived",
  out: "left",
  park: "parked in eMariner",
  reactivation: "reactivated in eMariner",
};

/** The What cell: "arrived", "left", "parked in eMariner" or "reactivated in eMariner". */
export const whatText = (entry: UkhoTimelineEntry): string =>
  WHAT_TEXT[entry.kind];

/** "10:12:40 UK": an arriving notice's transfer time on the UK clock. */
export const ukTimeText = (iso: string): string =>
  `${dayjs.utc(iso).tz("Europe/London").format("HH:mm:ss")} UK`;

const MATCHED_BY_TEXT: Partial<Record<IUkhoNoticeRow["matchBasis"], string>> = {
  licence: "licence number",
  imo: "IMO",
  manual: "by hand",
};

/** The Matched by cell: "licence number", "IMO" or "by hand"; "n/a" otherwise. */
export const matchedByText = (basis: IUkhoNoticeRow["matchBasis"]): string =>
  MATCHED_BY_TEXT[basis] ?? "n/a";

/** "3 notices in 2026": the view's notices of that year, whatever they move. */
export const noticesInYearText = (
  notices: IUkhoNoticeRow[],
  year: number
): string => {
  const count = notices.filter((notice) =>
    notice.noticeDate.startsWith(`${year}-`)
  ).length;
  return `${count} ${count === 1 ? "notice" : "notices"} in ${year}`;
};
